/**
 * Order handover (Req 28.2, design: `POST /ops/orders/:id/handover`): a `dispatched`
 * catalogue order becomes `delivered` at this outlet. The contract identifies the order
 * only by its id, so the operator scans it (a QR whose text is the order UUID) or types
 * it; the API checks the status and the pickup outlet. Online only.
 */
import { useId, useRef, useState, type FormEvent } from 'react';
import { formatPoints } from '@macksons/i18n';
import { asApiError, callOperation } from '@macksons/web-client';
import { shortId } from '../auth/operator-scope';
import { QrScanner } from '../components/QrScanner';
import { OnlineOnlyNotice, ResultCard, useErrorText, useOnline, type Feedback } from '../components/ui';
import { useLocale, useT } from '../hooks';
import { classifyScan, isUuid } from '../scanner/inputs';
import { scanMismatchText } from '../scanner/messages';
import { useServices } from '../services';
import type { Order } from '../types';

export function HandoverPage({ outletId }: { outletId: string }) {
  const t = useT();
  const locale = useLocale();
  const errorText = useErrorText();
  const { client, config } = useServices();
  const online = useOnline();
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [orderId, setOrderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>();
  const handover = async (raw: string) => {
    const value = raw.trim().toLowerCase();
    if (!isUuid(value)) {
      setFeedback({ tone: 'error', text: t('operator.handover.invalid') });
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const order = await callOperation<Order>(client, 'handoverOrder', { pathParams: { id: value }, body: { outletId } });
      setFeedback({
        tone: 'ok',
        text: t('operator.handover.delivered', { id: shortId(order.id), points: formatPoints(BigInt(order.pointCost), locale) }),
      });
      setOrderId('');
    } catch (cause) {
      const err = asApiError(cause);
      const text =
        err.code === 'invalid_transition'
          ? t('operator.handover.notDispatched')
          : err.code === 'not_found'
            ? t('operator.handover.notFound')
            : err.code === 'validation_error' && err.fields.some((f) => f.path === 'outletId')
              ? t('operator.handover.wrongOutlet')
              : errorText(err);
      setFeedback({ tone: 'error', text });
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void handover(orderId);
  };
  const onScan = (text: string) => {
    const scan = classifyScan(text, config.qrHost);
    if (scan.kind === 'order') {
      setOrderId(scan.orderId);
      void handover(scan.orderId);
    } else setFeedback({ tone: 'error', text: scanMismatchText(t, scan, 'order') });
  };
  return (
    <>
      <h1>{t('operator.handover.title')}</h1>
      <section className="panel">
        {!online && <OnlineOnlyNotice />}
        <ResultCard feedback={feedback} testId="handover" />
        <QrScanner onScan={onScan} primary startLabel={t('operator.handover.scan')} disabled={!online || busy} />
        <form onSubmit={submit} noValidate className="form">
          <div className="field">
            <label htmlFor={`${id}-order`}>{t('operator.handover.orderId')}</label>
            <p id={`${id}-hint`} className="hint">
              {t('operator.handover.hint')}
            </p>
            <input
              ref={inputRef}
              id={`${id}-order`}
              className="input"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              aria-describedby={`${id}-hint`}
              autoComplete="off"
              spellCheck={false}
              disabled={!online}
            />
          </div>
          <div className="sticky-action">
            <button type="submit" className="button button--primary button--block" disabled={!online || busy}>
              {t('operator.handover.submit')}
            </button>
            {!online && (
              <p className="hint" data-testid="handover-disabled-hint">
                {t('operator.offline.needsConnection')}
              </p>
            )}
          </div>
        </form>
      </section>
    </>
  );
}
